import { getFoilColorName, templateToTitle } from '@apps/card/utils';
import { formatCardSizeName, useCardSizeLabels } from '@apps/card/utils/cardSizeName';
import useCalculatePrintOrderPrice from '@apps/card/hooks/useCalculatePrintOrderPrice';
import { useFoilConfig } from '@apps/card/hooks/useFoilConfig';
import type { CardPriceModifiers, PriceConfig } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import type { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import type { PriceSummaryData } from './PriceSummary.types';
import { mapDeliveryMethod } from './utils/mapDeliveryMethod';
import { capitalize } from './utils/capitalize';
import { usePaperTypes } from '@apps/card/hooks/usePaperTypes';

export type PriceUpTo = 'cardFront' | 'cardBack' | 'envelope' | 'total';
export type CardPromotion = {
  code: string;
  /**
   * Saved as a percentage, e.g. 0.1 for 10%
   * Only saved on the draft.
   */
  discountPercentage?: Maybe<number>;
  /**
   * Only saved on the print order.
   */
  discountAmountInMinorUnits?: Maybe<number>;
};

/**
 * @deprecated Legacy pricing system. Please use useProductPriceSummaryController.
 */
export const usePriceSummaryController = (
  templateCategory: StationeryTemplateCategoryEnum,
  priceUpTo: PriceUpTo,
  config: PriceConfig,
  customizations: CardPriceModifiers,
  promotion?: Maybe<CardPromotion>,
  salesTax?: Maybe<number>,
  cardSize?: {
    width: number;
    height: number;
  },
): PriceSummaryData => {
  const {
    pricePerCard,
    pricePerReturnAddress,
    pricePerRecipientAddress,
    totalFoilPrice,
    totalPricePerQuantity,
    totalPricePerCardShape,
    totalReturnAddressPrice,
    totalRecipientAddressPrice,
    totalDeliveryPrice,
    totalPaperSizePrice,
    totalPaperTypePrice,
    totalEnvelopesPrice,
    totalBackLayoutPrice,
    total,
    discountedTotal,
    discountPercentage,
    totalInMinorUnits,
    tax,
  } = useCalculatePrintOrderPrice(templateCategory, priceUpTo, config, customizations, promotion, salesTax);

  const paperTypes = usePaperTypes();

  const envelopeItems: Array<[label: string, value: string | undefined]> = [
    [`${customizations.quantity} White Signature Envelopes`, totalEnvelopesPrice],
    [`Return Address: ${capitalize(customizations.returnAddress)} ${pricePerReturnAddress}`, totalReturnAddressPrice],
  ];

  const sizeLabels = useCardSizeLabels();
  const cardSizeText = formatCardSizeName(templateCategory, cardSize?.width ?? 5, cardSize?.height ?? 7, sizeLabels);

  const foilConfig = useFoilConfig();
  const foilItems: Array<[label: string, value: string | undefined]> = customizations.foilData
    ? [[`Foil: ${getFoilColorName(customizations.foilData.colorId, foilConfig.offerings)}`, totalFoilPrice]]
    : [];

  return {
    card: [
      [`Quantity: ${customizations.quantity} cards ${pricePerCard} each`, totalPricePerQuantity],
      [`Silhouette: ${capitalize(customizations.shape)}`, totalPricePerCardShape],
      [`Paper Type: ${paperTypes[customizations.paperType]?.label}`, totalPaperTypePrice],
      [`Size: ${cardSizeText}`, totalPaperSizePrice],
      ...foilItems,
    ],
    back: customizations.backLayout === 'none' ? [] : [[`Layout: ${templateToTitle({ themeId: customizations.backLayout })}`, totalBackLayoutPrice]],
    envelope: customizations.recipientAddress
      ? [...envelopeItems, [`Recipient Address: ${capitalize(customizations.recipientAddress)} ${pricePerRecipientAddress}`, totalRecipientAddressPrice]]
      : envelopeItems,
    shipping: [[`${capitalize(mapDeliveryMethod(customizations.delivery) || '')} Shipping`.trim(), totalDeliveryPrice]],
    tax: [[`Sales Tax`, tax]],
    total,
    totalInMinorUnits,
    discountedTotal,
    discountPercentage,
    pricePerCard,
  };
};
